
import {
  Calendar,
  CheckCircle2,
  CreditCard,
  Download,
  Eye,
  FileText,
  Filter,
  Gift,
  IndianRupee,
  MessageSquare,
  PackageCheck,
  Printer,
  Search,
  Tag,
  Truck,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { cn, formatPrice, getInitials } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/Modal";
import { OrderInvoice } from "@/components/admin/OrderInvoice";
import { ActionDropdown } from "@/components/ui/ActionDropdown";

const tabs = ["All", "Pending", "Packed", "Shipped", "Delivered", "Cancelled"];

// Each tab gets a unified solid dark navy/black active style with a translucent badge
const tabStyles = {
  All: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  Pending: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  Packed: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  Shipped: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  Delivered: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  Cancelled: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
  default: {
    active: "bg-[#0f172a] text-white shadow-md shadow-[#0f172a]/20",
    count: "bg-white/20 text-white",
  },
};

const paymentTone = (status) => {
  if (status === "Paid") return "green";
  if (status === "Pending") return "gold";
  if (status === "Refunded") return "muted";
  return "red";
};

const statusTone = (status) => {
  switch (status) {
    case "Delivered":
      return "green";
    case "Shipped":
      return "gold";
    case "Packed":
      return "gold";
    case "Pending":
      return "muted";
    case "Cancelled":
      return "red";
    default:
      return "muted";
  }
};

function WhatsAppIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 256 256" fill="currentColor">
      <path
        fill="#25D366"
        d="M128 0C57.308 0 0 57.308 0 128c0 22.6 5.86 44.62 17.02 63.95L1.83 247.96a12 12 0 0014.21 14.21l56.01-15.19C91.38 250.14 113.4 256 128 256c70.692 0 128-57.308 128-128S198.692 0 128 0z"
      />
      <path
        fill="#FFF"
        d="M191.49 161.73c-3.14-1.57-18.57-9.17-21.46-10.22-2.88-1.05-4.99-1.57-7.09 1.57-2.1 3.14-8.13 10.22-9.97 12.32-1.84 2.1-3.68 2.36-6.82.79-3.14-1.57-13.27-4.89-25.28-15.6-9.35-8.33-15.66-18.62-17.5-21.76-1.84-3.14-.19-4.84 1.38-6.4 1.41-1.41 3.14-3.68 4.71-5.52 1.57-1.84 2.1-3.14 3.14-5.24 1.05-2.1.52-3.94-.26-5.52-.79-1.57-7.09-17.08-9.71-23.39-2.55-6.14-5.14-5.31-7.09-5.41-1.84-.09-3.94-.1-6.04-.1-2.1 0-5.52.79-8.4 3.94-2.88 3.14-11.02 10.77-11.02 26.26 0 15.49 11.28 30.46 12.85 32.56 1.57 2.1 22.21 33.92 53.8 47.58 7.51 3.25 13.38 5.19 17.95 6.64 7.54 2.4 14.4 2.06 19.82 1.25 6.04-.9 18.57-7.59 21.19-14.92 2.62-7.33 2.62-13.62 1.84-14.92-.78-1.31-2.88-2.09-6.02-3.66z"
      />
    </svg>
  );
}

export function OrderTable() {
  const { orders, updateOrderStatus, markOrderAsSeen } = useStore();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryParam = searchParams.get("q") || "";

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("All");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [viewingOrder, setViewingOrder] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  useEffect(() => {
    if (queryParam) {
      setSearch(queryParam);
    }
  }, [queryParam]);

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    if (order._id) {
      markOrderAsSeen(order._id);
    }
  };

  // WhatsApp message state
  const [whatsAppModalOrder, setWhatsAppModalOrder] = useState(null);
  const [customWhatsAppMessage, setCustomWhatsAppMessage] = useState("");

  const openWhatsAppModal = (order) => {
    const customerName =
      order.customer || order.shippingAddress?.fullName || "Customer";
    const defaultMsg = `Hello ${customerName}! ✨ Your order #${order.orderId} has been confirmed by Alaira House. Total: ₹${order.total}. We are preparing your gift with care! 🎁`;
    setCustomWhatsAppMessage(defaultMsg);
    setWhatsAppModalOrder(order);
  };

  const sendWhatsAppMessage = () => {
    if (!whatsAppModalOrder) return;
    const rawPhone =
      whatsAppModalOrder.shippingAddress?.phone ||
      whatsAppModalOrder.phone ||
      "";
    let cleanPhone = rawPhone.replace(/\D/g, "");
    if (cleanPhone.length === 10) {
      cleanPhone = `91${cleanPhone}`;
    }
    const encodedText = encodeURIComponent(customWhatsAppMessage);
    window.open(
      `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`,
      "_blank",
    );
    toast.success(
      `WhatsApp message opened for ${whatsAppModalOrder.customer}! 💬`,
    );
    setWhatsAppModalOrder(null);
  };

  const counts = useMemo(() => {
    const map = { All: orders.length };
    tabs.forEach((t) => {
      if (t === "All") return;
      map[t] = orders.filter((order) => order.deliveryStatus === t).length;
    });
    return map;
  }, [orders]);

  const filtered = useMemo(() => {
    const statusPriority = {
      Pending: 1,
      Packed: 2,
      Shipped: 3,
      Delivered: 4,
      Cancelled: 5,
    };

    const list = orders.filter((order) => {
      const matchesSearch =
        order.orderId.toLowerCase().includes(search.toLowerCase()) ||
        order.customer.toLowerCase().includes(search.toLowerCase()) ||
        order.phone.includes(search) ||
        (order.userEmail || "").toLowerCase().includes(search.toLowerCase());
      const matchesTab = tab === "All" || order.deliveryStatus === tab;
      const matchesPayment =
        paymentFilter === "All" || order.paymentStatus === paymentFilter;
      return matchesSearch && matchesTab && matchesPayment;
    });

    return list.sort((a, b) => {
      const pA = statusPriority[a.deliveryStatus] || 99;
      const pB = statusPriority[b.deliveryStatus] || 99;
      if (pA !== pB) return pA - pB;
      const dateA = new Date(a.createdAt || a.date).getTime() || 0;
      const dateB = new Date(b.createdAt || b.date).getTime() || 0;
      return dateB - dateA;
    });
  }, [orders, paymentFilter, search, tab]);

  // Totals for the header KPIs
  const revenue = useMemo(
    () =>
      orders
        .filter((order) => order.paymentStatus === "Paid")
        .reduce((sum, order) => sum + order.total, 0),
    [orders],
  );

  const handlePrint = (order) => {
    setViewingInvoice(order);
    setTimeout(() => {
      window.print();
    }, 200);
  };

  const advanceStatus = (order) => {
    const order1 = ["Pending", "Packed", "Shipped", "Delivered"];
    const currentIndex = order1.indexOf(order.deliveryStatus);
    if (currentIndex === -1 || currentIndex === order1.length - 1) return;
    const nextStatus = order1[currentIndex + 1];
    updateOrderStatus(order._id, nextStatus);
  };

  return (
    <div className="no-print space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748b]">
            Order management
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight text-[#0f172a] sm:text-4xl">
            Orders &amp; deliveries
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748b]">
            View every order, update delivery status, print professional
            invoices, and follow up with customers — all from one queue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white px-4 py-2.5 shadow-sm">
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#64748b]">
              Paid revenue
            </p>
            <p className="font-display text-lg font-bold text-[#0f172a]">
              {formatPrice(revenue)}
            </p>
          </div>
          <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white px-4 py-2.5 shadow-sm">
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#64748b]">
              Orders
            </p>
            <p className="font-display text-lg font-bold text-[#0f172a]">
              {orders.length}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs — each tab has a distinct accent color */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#e2e8f0] bg-white p-2 shadow-sm">
        {tabs.map((t) => {
          const active = t === tab;
          // Per-tab distinct color treatment (subtle, premium)
          const tabColor = tabStyles[t] || tabStyles.default;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "flex items-center gap-2 rounded-xl px-3.5 py-2 text-[11px] font-bold uppercase tracking-widest transition duration-300",
                active
                  ? tabColor.active
                  : "text-[#64748b] hover:bg-[#f8fafc] hover:text-[#0f172a]",
              )}
            >
              {t}
              <span
                className={cn(
                  "rounded-md px-1.5 text-[10px] font-black tabular-nums",
                  active ? tabColor.count : "bg-[#f1f5f9] text-[#64748b]",
                )}
              >
                {counts[t] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3 text-sm focus-within:border-[#0f172a]">
          <Search size={16} className="text-[#64748b]" />
          <input
            type="search"
            name="admin_order_search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search orders by ID, name, or phone…"
            className="h-full w-full bg-transparent outline-none"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#64748b] md:inline-flex">
            <Filter size={12} /> Payment
          </span>
          <select
            value={paymentFilter}
            onChange={(event) => setPaymentFilter(event.target.value)}
            className="h-11 rounded-xl border border-[#e2e8f0] bg-white px-3 text-xs font-bold uppercase tracking-widest outline-none focus:border-[#0f172a]"
          >
            <option value="All">All payments</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
            <option value="Refunded">Refunded</option>
          </select>
        </div>
      </div>

      {/* Order list */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#e2e8f0] bg-white py-16 text-center">
          <Truck className="mx-auto mb-3 text-[#64748b]" size={28} />
          <p className="font-display text-xl font-semibold text-[#0f172a]">
            No orders match this filter
          </p>
          <p className="mt-1 text-sm text-[#64748b]">
            Try a different status or clear the search.
          </p>
        </div>
      ) : (
        <ul className="grid gap-3">
          {filtered.map((order) => (
            <li
              key={order._id}
              className="grid gap-3 rounded-2xl border border-[#e2e8f0]/60 bg-white p-4 shadow-sm transition hover:shadow-md md:grid-cols-[1fr_auto] md:items-center"
            >
              <div
                className="grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-center cursor-pointer group"
                onClick={() => handleViewOrder(order)}
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-[#0f172a]/10 to-gold/10 text-sm font-bold text-[#0f172a] group-hover:scale-105 transition">
                  {getInitials(order.customer || "Customer")}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-base font-bold text-[#0f172a] group-hover:underline">
                      {order.orderId}
                    </p>
                    <Badge
                      tone={statusTone(order.deliveryStatus)}
                      className="text-[9px] font-black uppercase"
                    >
                      {order.deliveryStatus}
                    </Badge>
                    <Badge
                      tone={paymentTone(order.paymentStatus)}
                      className="text-[9px] font-black uppercase"
                    >
                      {order.paymentStatus}
                    </Badge>
                  </div>
                  <p
                    className="mt-1 truncate text-sm font-semibold text-[#0f172a]"
                    style={{ wordBreak: "break-word" }}
                  >
                    {order.customer || order.shippingAddress?.fullName || "Guest Customer"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#64748b]">
                    {order.userEmail || "—"} · {order.phone} ·{" "}
                    {(() => {
                      const d = new Date(order.createdAt || order.date);
                      return isNaN(d.getTime())
                        ? "Recently"
                        : d.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });
                    })()}
                  </p>
                  <p className="mt-1 line-clamp-1 text-[11px] text-[#64748b]">
                    {(order.products || [])
                      .map((item) => `${item.title || item.name || "Item"} ×${item.quantity || item.qty || 1}`)
                      .join(" · ")}
                  </p>

                  {/* Gift Note / Customer Message */}
                  {(order.giftNote || order.note || order.customerNote) && (
                    <div className="mt-2.5 flex items-start gap-2 rounded-xl border border-dashed border-[#F7C7DC] bg-[#FFF4F7] p-2.5 text-xs text-[#1C1924]">
                      <Gift size={14} className="mt-0.5 shrink-0 text-[#E5497C]" />
                      <div className="min-w-0">
                        <span className="font-bold text-[#E5497C]">Gift Note:</span>{" "}
                        <span className="italic font-serif">"{order.giftNote || order.note || order.customerNote}"</span>
                        {(order.envelopeFrontText || order.letterStyle) && (
                          <p className="mt-0.5 text-[10px] text-[#64748b]">
                            Envelope Front: <span className="font-semibold text-[#0f172a]">{order.envelopeFrontText || order.letterStyle}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-right sm:ml-4">
                  <p className="font-display text-lg font-bold text-[#0f172a]">
                    {formatPrice(order.total)}
                  </p>
                  {order.couponCode ? (
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#0f172a]">
                      {order.couponCode} · −{formatPrice(order.couponDiscount)}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <select
                  value={order.deliveryStatus}
                  onChange={(event) =>
                    updateOrderStatus(order._id, event.target.value)
                  }
                  className="h-9 rounded-full border border-[#e2e8f0] bg-white px-3 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#0f172a]"
                >
                  {[
                    "Pending",
                    "Packed",
                    "Shipped",
                    "Delivered",
                    "Cancelled",
                  ].map((status) => (
                    <option key={status} value={status}>
                      Mark {status}
                    </option>
                  ))}
                </select>
                {order.deliveryStatus !== "Delivered" &&
                  order.deliveryStatus !== "Cancelled" && (
                    <button
                      type="button"
                      onClick={() => advanceStatus(order)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#0f172a] px-3 text-[10px] font-black uppercase tracking-widest text-white hover:bg-[#5d0710]"
                    >
                      <CheckCircle2 size={12} /> Advance
                    </button>
                  )}
                <ActionDropdown
                  items={[
                    ...(order.deliveryStatus === "Pending"
                      ? [
                          {
                            label: "WhatsApp customer",
                            icon: MessageSquare,
                            onClick: () => openWhatsAppModal(order),
                            variant: "gold",
                          },
                        ]
                      : []),
                    {
                      label: "View order",
                      icon: Eye,
                      onClick: () => handleViewOrder(order),
                    },
                    {
                      label: "View invoice",
                      icon: FileText,
                      onClick: () => setViewingInvoice(order),
                    },
                    {
                      label: "Print invoice",
                      icon: Printer,
                      onClick: () => handlePrint(order),
                    },
                    {
                      label: "Download PDF",
                      icon: Download,
                      onClick: () => handlePrint(order),
                    },
                    {
                      label:
                        order.deliveryStatus === "Cancelled"
                          ? "Restore order"
                          : "Cancel order",
                      icon: XCircle,
                      onClick: () => updateOrderStatus(order._id, "Cancelled"),
                    },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Order details modal */}
      {viewingOrder && (
        <Modal
          open={!!viewingOrder}
          onClose={() => setViewingOrder(null)}
          title={`Order ${viewingOrder.orderId}`}
          className="max-w-3xl"
        >
          <div className="space-y-5 py-1">
            <div className="grid gap-2.5 grid-cols-2 sm:grid-cols-4">
              <OrderStat
                label="Date"
                value={(() => {
                  const d = new Date(viewingOrder.createdAt || viewingOrder.date);
                  return isNaN(d.getTime())
                    ? "Recently"
                    : d.toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      });
                })()}
                icon={Calendar}
              />

              <OrderStat
                label="Payment"
                value={viewingOrder.paymentStatus}
                icon={CreditCard}
                accent={paymentTone(viewingOrder.paymentStatus)}
              />

              <OrderStat
                label="Delivery"
                value={viewingOrder.deliveryStatus}
                icon={PackageCheck}
                accent={statusTone(viewingOrder.deliveryStatus)}
              />

              <OrderStat
                label="Total"
                value={
                  <span className="font-sans tabular-nums price">
                    {formatPrice(viewingOrder.total)}
                  </span>
                }
                icon={IndianRupee}
                accent="gold"
              />
            </div>

            {/* Gift Note / Customer Message Block */}
            {(viewingOrder.giftNote || viewingOrder.note || viewingOrder.customerNote) && (
              <div className="rounded-2xl border border-dashed border-[#F7C7DC] bg-[#FFF4F7] p-4 text-sm text-[#1C1924]">
                <div className="flex items-center gap-2 font-bold text-[#E5497C] text-xs uppercase tracking-wider mb-1">
                  <Gift size={15} /> Personalized Gift Note / Customer Message
                </div>
                <p className="font-serif italic text-base text-[#1C1924] leading-relaxed">
                  "{viewingOrder.giftNote || viewingOrder.note || viewingOrder.customerNote}"
                </p>
                {(viewingOrder.envelopeFrontText || viewingOrder.letterStyle) && (
                  <p className="mt-2 text-xs text-[#64748b]">
                    <span className="font-semibold text-[#0f172a]">Envelope Front Calligraphy:</span> {viewingOrder.envelopeFrontText || viewingOrder.letterStyle}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-2xl border border-[#e2e8f0]/60 bg-[#f8fafc]/30 p-4">
                <h3 className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748b]">
                  Customer
                </h3>
                <p className="truncate text-sm font-semibold text-[#0f172a]">
                  {viewingOrder.customer || viewingOrder.shippingAddress?.fullName || "Guest Customer"}
                </p>
                <p
                  className="mt-1 truncate text-[11px] text-[#64748b]"
                  title={viewingOrder.userEmail || ""}
                >
                  {viewingOrder.userEmail || "—"}
                </p>
                <p className="mt-1 text-[11px] text-[#64748b]">
                  📞 {viewingOrder.phone || viewingOrder.shippingAddress?.phone}
                </p>
              </div>
              <div className="rounded-2xl border border-[#e2e8f0]/60 bg-[#f8fafc]/30 p-4">
                <h3 className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748b]">
                  Shipping address
                </h3>
                <p className="truncate text-sm font-semibold text-[#0f172a]">
                  {viewingOrder.shippingAddress?.fullName || viewingOrder.customer}
                </p>
                <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-[#64748b]">
                  {viewingOrder.shippingAddress?.address || "No street address"}
                  {viewingOrder.shippingAddress?.landmark
                    ? `, ${viewingOrder.shippingAddress.landmark}`
                    : ""}
                  , {viewingOrder.shippingAddress?.city || "City"},{" "}
                  {viewingOrder.shippingAddress?.state || "State"} —{" "}
                  {viewingOrder.shippingAddress?.pincode || "000000"}
                </p>
                <p className="mt-1 text-[11px] text-[#64748b]">
                  📞 {viewingOrder.shippingAddress?.phone || viewingOrder.phone}
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-[#e2e8f0]/60">
              <div className="flex items-center justify-between bg-[#f8fafc]/40 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748b]">
                <span>Order items ({viewingOrder.products.length})</span>
                <Tag size={12} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-left text-sm">
                  <thead className="bg-white text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                    <tr>
                      <th className="px-4 py-2">Product</th>
                      <th className="w-16 px-2 py-2 text-center">Qty</th>
                      <th className="w-28 px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border/40">
                    {viewingOrder.products.map((p, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 flex items-center gap-3">
                          {p.image ? (
                            <img
                              src={p.image}
                              alt={p.title}
                              className="h-10 w-10 rounded-lg object-cover border border-[#e2e8f0] shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-[#f1f5f9] flex items-center justify-center text-xs font-bold text-[#64748b] shrink-0">
                              🎁
                            </div>
                          )}
                          <div className="min-w-0">
                            <p
                              className="truncate font-semibold text-[#0f172a]"
                              title={p.title || p.name}
                            >
                              {p.title || p.name || "Gift Product"}
                            </p>
                            {p.color || p.size ? (
                              <p className="mt-0.5 text-[10px] text-[#64748b]">
                                {p.color} {p.size ? `· ${p.size}` : ""}
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-center font-bold text-[#0f172a]">
                          {p.quantity || p.qty || 1}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-[#0f172a]">
                          {formatPrice(p.price * p.quantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#f8fafc]/20 text-sm">
                    <tr>
                      <td
                        colSpan={2}
                        className="px-4 py-2 text-right text-[#64748b]"
                      >
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-[#0f172a]">
                        {formatPrice(viewingOrder.subtotal)}
                      </td>
                    </tr>
                    {viewingOrder.couponDiscount > 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-4 py-2 text-right text-[#0f172a]"
                        >
                          Coupon ({viewingOrder.couponCode})
                        </td>
                        <td className="px-4 py-2 text-right font-bold text-[#0f172a]">
                          -{formatPrice(viewingOrder.couponDiscount)}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t border-[#e2e8f0]/40">
                      <td
                        colSpan={2}
                        className="px-4 py-3 text-right font-display text-sm font-bold text-[#0f172a]"
                      >
                        Grand total
                      </td>
                      <td className="px-4 py-3 text-right font-display text-base font-bold text-[#0f172a]">
                        <span className="font-sans tabular-nums price">
                          {formatPrice(viewingOrder.total)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[#e2e8f0]/40 pt-4">
              <button
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0f172a] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-[#0a0a0a]/15 hover:bg-[#5d0710]"
                onClick={() => {
                  setViewingInvoice(viewingOrder);
                  setViewingOrder(null);
                }}
              >
                <FileText size={14} /> View invoice
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-full border border-[#e2e8f0] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0f172a] hover:bg-[#f8fafc]"
                onClick={() => handlePrint(viewingOrder)}
              >
                <Printer size={14} /> Print
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Invoice modal */}
      {viewingInvoice && (
        <Modal
          open={!!viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          title="Invoice preview"
          className="max-w-[850px] p-0 overflow-hidden"
        >
          <div className="max-h-[80vh] overflow-y-auto bg-[#f8fafc]/10">
            <OrderInvoice order={viewingInvoice} />
          </div>
          <div className="flex items-center justify-between border-t border-[#e2e8f0] bg-white p-5 no-print">
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#0f172a]" />
              A4 print ready
            </span>
            <div className="flex gap-2">
              <button
                className="rounded-full border border-[#e2e8f0] px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[#0f172a] hover:bg-[#f8fafc]"
                onClick={() => setViewingInvoice(null)}
              >
                Close
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-full bg-[#0f172a] px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#0a0a0a]/15 hover:bg-[#5d0710]"
                onClick={() => window.print()}
              >
                <Printer size={12} /> Print now
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* WhatsApp Editable Pre-filled Message Modal */}
      {whatsAppModalOrder && (
        <Modal
          open={!!whatsAppModalOrder}
          onClose={() => setWhatsAppModalOrder(null)}
          title={`WhatsApp Update — Order ${whatsAppModalOrder.orderId}`}
          className="max-w-lg"
        >
          <div className="space-y-4 py-2">
            <div className="rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#25D366] text-white grid place-items-center shrink-0 font-bold">
                WA
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-[#0f172a]">
                  Customer: {whatsAppModalOrder.customer || whatsAppModalOrder.shippingAddress?.fullName}
                </p>
                <p className="text-[11px] text-[#64748b]">
                  Phone: 📞 {whatsAppModalOrder.shippingAddress?.phone || whatsAppModalOrder.phone}
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] block mb-1.5">
                Pre-filled Editable Message:
              </label>
              <textarea
                rows={5}
                value={customWhatsAppMessage}
                onChange={(e) => setCustomWhatsAppMessage(e.target.value)}
                className="w-full rounded-xl border border-[#e2e8f0] p-3 text-sm outline-none transition focus:border-[#25D366] leading-relaxed font-sans"
                placeholder="Type custom message to customer..."
              />
              <p className="mt-1 text-[11px] text-[#64748b]">
                You can edit this message freely before sending on WhatsApp.
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setWhatsAppModalOrder(null)}
                className="flex-1 rounded-full border border-[#e2e8f0] py-2.5 text-xs font-bold uppercase tracking-widest text-[#64748b] hover:bg-[#f8fafc]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendWhatsAppMessage}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-md hover:bg-[#1eb956] transition cursor-pointer"
              >
                <MessageSquare size={15} /> Send via WhatsApp
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function OrderStat({ label, value, icon: Icon, accent = "muted" }) {
  const accentMap = {
    muted: "border-[#e2e8f0] bg-white text-[#0f172a]",
    gold: "border-[#0f172a]/15 bg-[#f8fafc] text-[#0f172a]",
    green: "border-[#0f172a]/15 bg-[#f1f5f9] text-[#0f172a]",
    red: "border-[#0f172a]/15 bg-[#0f172a]/5 text-[#0f172a]",
  };
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-xl border px-3 py-2.5 min-w-0",
        accentMap[accent],
      )}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/70">
        <Icon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[9px] font-bold uppercase tracking-widest opacity-70">
          {label}
        </p>
        <p className="truncate text-sm font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}
